(() => {
  window.__VARIANT_MEDIA_MANAGED__ = true;

  const MAP_ID = "variant-media-map";
  const EVENT_MEDIA_UPDATED = "product-info:media-updated";

  const norm = (s) => String(s || "").trim().toUpperCase();

  /* ======================================================
     CSS MASK (prevents ALL visible DOM churn)
  ====================================================== */
  function ensureCSS() {
    if (document.getElementById("vmf-css")) return;
    const style = document.createElement("style");
    style.id = "vmf-css";
    style.textContent = `
      .product__media-wrapper{
        position:relative;
        isolation:isolate;
      }
      .product__media-wrapper::after{
        content:"";
        position:absolute;
        inset:0;
        background: rgba(255,255,255,0.001);
        opacity:0;
        pointer-events:none;
        z-index:9999;
        transition: opacity .18s ease;
      }
      .product__media-wrapper.vmf-switching::after{
        opacity:1;
        transition:none;
      }
      .vmf-hidden{ display:none !important; }
      .vmf-slide-hidden{
        opacity:0 !important;
        pointer-events:none !important;
        position:absolute !important;
        width:1px !important;
        height:1px !important;
        overflow:hidden !important;
      }
    `;
    document.head.appendChild(style);
  }

  function getMapEl() {
    return document.getElementById(MAP_ID);
  }

  function getRoot() {
    return getMapEl()?.closest("product-info") || document;
  }

  function getWrap(root) {
    return root.querySelector(".product__media-wrapper");
  }

  function maskOn(root) {
    ensureCSS();
    getWrap(root)?.classList.add("vmf-switching");
  }

  function maskOff(root) {
    getWrap(root)?.classList.remove("vmf-switching");
  }

  /* ======================================================
     MAP + FILENAME NORMALIZATION
  ====================================================== */
  function getMap() {
    const el = getMapEl();
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  function filenameFromSrc(src) {
    try {
      return new URL(src, location.href).pathname.split("/").pop() || "";
    } catch {
      return String(src).split("?")[0].split("/").pop() || "";
    }
  }

  function mediaKey(src) {
    let fn = filenameFromSrc(src);
    fn = fn.replace(/\.webp$/i, "");
    fn = fn.replace(/@\dx(?=\.)/i, "");
    fn = fn.replace(/_(\d+x\d+|\d+x|x\d+).*$/i, "");
    return norm(fn);
  }

  /* ======================================================
     COLOR SELECTION
  ====================================================== */
  function getSelectedColor(root) {
    const legend = [...root.querySelectorAll("legend")]
      .find(l => norm(l.textContent).includes("COLOR"));
    const fs = legend?.closest("fieldset");

    const radio = fs?.querySelector("input[type=radio]:checked");
    if (radio) return radio.value;

    const select = fs?.querySelector("select");
    if (select) return select.value;

    return null;
  }

  /* ======================================================
     GALLERY ACCESS
  ====================================================== */
  function getGallery(root) {
    return root.querySelector("media-gallery");
  }

  function getSlides(root, map) {
    const universe = new Set(Object.values(map).flat().map(mediaKey));
    const ul = getGallery(root)?.querySelector("ul");
    if (!ul) return [];

    return [...ul.querySelectorAll("li[data-media-id]")]
      .map(li => {
        const img = li.querySelector("img");
        if (!img) return null;
        const fn = mediaKey(img.currentSrc || img.src);
        if (!universe.has(fn)) return null;
        return { li, fn, mediaId: li.dataset.mediaId };
      })
      .filter(Boolean);
  }

  function getThumbs(root, map) {
    const universe = new Set(Object.values(map).flat().map(mediaKey));
    return [...root.querySelectorAll("li.thumbnail-list__item")]
      .map(li => {
        const img = li.querySelector("img");
        if (!img) return null;
        const fn = mediaKey(img.currentSrc || img.src);
        if (!universe.has(fn)) return null;
        return { li, fn };
      })
      .filter(Boolean);
  }

  function pickFlatlay(color, allowed) {
    const c = norm(color);
    return (
      allowed.find(k => k.startsWith(c + "_") && k.includes("_FLATLAY")) ||
      allowed.find(k => k.includes("_FLATLAY")) ||
      allowed[0]
    );
  }

  /* ======================================================
     ACTIVE MEDIA (NO CLICKING)
  ====================================================== */
  function setActiveMedia(gallery, mediaId, root) {
    if (!gallery?.setActiveMedia) return;
    const section = root.dataset.section || root.dataset.originalSection || "";
    const ids = section ? [`${section}-${mediaId}`, mediaId] : [mediaId];

    for (const id of ids) {
      try {
        gallery.setActiveMedia(id, true);
        return;
      } catch {}
    }
  }

  function waitForActive(gallery, mediaId, root, done) {
    const section = root.dataset.section || root.dataset.originalSection || "";
    const ids = section ? [`${section}-${mediaId}`, mediaId] : [mediaId];

    const isActive = () => {
      const active = gallery.querySelector("li.is-active,[aria-current='true']");
      if (!active) return false;
      const id = active.dataset.mediaId || "";
      return ids.includes(id) || ids.includes(id.split("-").pop());
    };

    if (isActive()) return done();

    const mo = new MutationObserver(() => {
      if (isActive()) {
        mo.disconnect();
        done();
      }
    });

    mo.observe(gallery, { attributes:true, childList:true, subtree:true });
    setTimeout(() => { mo.disconnect(); done(); }, 350);
  }

  /* ======================================================
     APPLY FILTER (SINGLE PAINT)
  ====================================================== */
  let lastSig = "";

  function applyFilter() {
    const root = getRoot();
    const map = getMap();
    if (!map) return;

    const color = getSelectedColor(root);
    if (!color) return;

    const allowed = (map[color] || []).map(mediaKey);
    if (!allowed.length) return;

    const sig = color + "::" + allowed.join("|");
    if (sig === lastSig) return;
    lastSig = sig;

    const gallery = getGallery(root);
    if (!gallery) return;

    maskOn(root);

    const slides = getSlides(root, map);
    const thumbs = getThumbs(root, map);

    thumbs.forEach(t =>
      t.li.classList.toggle("vmf-hidden", !allowed.includes(t.fn))
    );

    slides.forEach(s => {
      s.li.classList.toggle("vmf-slide-hidden", !allowed.includes(s.fn));
      s.li.setAttribute("aria-hidden", allowed.includes(s.fn) ? "false" : "true");
    });

    const flatKey = pickFlatlay(color, allowed);
    const flatSlide = slides.find(s => s.fn === flatKey);

    if (flatSlide) {
      setActiveMedia(gallery, flatSlide.mediaId, root);
      waitForActive(gallery, flatSlide.mediaId, root, () => maskOff(root));
    } else {
      setTimeout(() => maskOff(root), 200);
    }
  }

  /* ======================================================
     SCHEDULING (NO TIMEOUT PAINT)
  ====================================================== */
  let raf;
  function scheduleApply() {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() =>
      requestAnimationFrame(applyFilter)
    );
  }

  function init() {
    ensureCSS();
    scheduleApply();

    document.addEventListener("change", e => {
      if (e.target.matches("input[type=radio],select")) scheduleApply();
    });

    document.addEventListener(EVENT_MEDIA_UPDATED, scheduleApply);
    document.addEventListener("shopify:section:load", scheduleApply);
    window.addEventListener("load", scheduleApply);
  }

  document.addEventListener("DOMContentLoaded", init);
})();
